import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { consumeOauthState } from "../authConfig";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeCode } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [stage, setStage] = useState("Processing Microsoft sign-in…");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // 1. Microsoft may have returned an error directly.
        const oauthError = searchParams.get("error");
        if (oauthError) {
          const desc = searchParams.get("error_description") || oauthError;
          throw new Error(desc);
        }

        // 2. Verify state to defend against CSRF.
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        if (!code) throw new Error("No authorization code returned by Microsoft.");
        if (!consumeOauthState(state)) {
          throw new Error("Sign-in state mismatch. Please try signing in again.");
        }

        if (cancelled) return;
        setStage("Validating identity with the server…");

        await exchangeCode(code);

        if (cancelled) return;
        setStage("Signed in. Redirecting…");
        navigate("/", { replace: true });
      } catch (err) {
        console.error("Auth callback error:", err);
        if (!cancelled) {
          const msg = err?.response?.data?.detail || err?.message || "Sign-in failed.";
          setError(typeof msg === "string" ? msg : "Sign-in failed.");
        }
      }
    })();

    return () => { cancelled = true; };
  }, [exchangeCode, navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="auth-callback-page">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {error ? (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle size={22} className="text-red-500" />
            </div>
            <h1 className="text-base font-bold text-slate-900 mb-1">Sign-in failed</h1>
            <p className="text-xs text-slate-600 mb-5 break-words">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              data-testid="callback-back-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#B71C1C] text-white rounded-lg text-xs font-semibold hover:bg-[#8B1515] transition-colors"
            >
              <ArrowLeft size={13} />
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <Loader2 size={28} className="animate-spin text-[#B71C1C] mx-auto mb-4" />
            <h1 className="text-base font-bold text-slate-900 mb-1">Just a moment</h1>
            <p className="text-xs text-slate-600">{stage}</p>
          </>
        )}
      </div>
    </div>
  );
}
